import * as THREE from "three";
import { Context } from "../core/context";
import {
  DBEntity,
  DbLayer,
  DbLine,
  DbMesh,
  FMathUtils,
  ViewManager,
  generateUUID,
} from "@/CadEngine";
import {
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
  IModifyOperation,
  ModifyOperateState,
} from "./IModifyOperation";
import { ULabel } from "@/ui/ULabel";
import {
  CommonColor,
  InsertConnectPointModifyAcceptParams,
} from "@/core/Parameters";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils";

class InsertConnectPointModify implements IModifyOperation {
  private m_context: Context;
  private m_points: THREE.Vector3[] = [];
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_TextLabel: ULabel;
  private m_locationObject: THREE.Line;
  private m_tempObjectGroup: THREE.Group = new THREE.Group();
  private m_acceptParams: InsertConnectPointModifyAcceptParams;
  private m_layer: DbLayer;
  private m_returnData: { object: DbMesh; point: THREE.Vector3 }[] = [];
  private m_color: number;

  constructor(context: Context, params: InsertConnectPointModifyAcceptParams) {
    this.m_context = context;
    this.m_acceptParams = params;
    this.m_layer = params.layer;
    this.m_color = params.color;
    this.m_TextLabel = this.m_context.ModifyManager.TextLabel;
    this.m_locationObject = this.m_context.ToolsManager.CreatedLocationLine();
    this.m_context.SceneManager.AuxModel.add(this.m_tempObjectGroup);
    this.m_TextLabel.Visibility = true;
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(point: DrawOperationMovePointParams): void {
    this.m_curPoint = point.point.clone();
    this.m_locationObject.position.copy(this.m_curPoint);
    let textLabelPosition = point.point.clone();
    textLabelPosition.x = this.m_curPoint.x + this.Scale * 45;
    textLabelPosition.y = this.m_curPoint.y - this.Scale * 10;
    this.m_TextLabel.SetVaule(
      "点击插入连接点,按Ctrl+Z键可撤销",
      textLabelPosition,
      new THREE.Vector3(1, 0, 0)
    );
  }
  OnDownPoint(point: DrawOperationMovePointParams): void {
    this.m_acceptParams.callbackUnderway(point.point.clone());
    this.m_points.push(point.point.clone());
    this.createTempObject(point.point);
  }
  onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    if (e.ctrlKey && (e.key == "z"||e.key=="Z")) {
      let delObject = this.m_tempObjectGroup.children.pop();
      this.m_context.ToolsManager.ObjectsClear([delObject!]);
      this.m_context.ViewManager.Render();
      this.m_points.pop();
    }
  }
  onKeyUp(e: KeyboardEvent): void {}

  OnZoom(camera: THREE.Camera): void {}

  Finish(operate: ModifyOperateState) {
    this.endHandle();
    this.clear();
    if (operate == ModifyOperateState.complete) {
      return this.m_returnData;
    } else if (operate == ModifyOperateState.cancel) {
      return [];
    }
  }
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e?: PointerEvent): void {}

  onMouseDown(e: PointerEvent): void {
    if (e.button !== 0) return;
  }

  onMouseUp(e: PointerEvent): void {}

  private createTempObject(point: THREE.Vector3) {
    let geo = new THREE.BufferGeometry();
    let radius = 0.05;
    let count = 50;
    let angle = (Math.PI * 2) / count;
    let positions: THREE.Vector3[] = [];
    for (let i = 0; i < count; i++) {
      let x1 = radius * Math.cos(angle * i) + point.x;
      let y1 = radius * Math.sin(angle * i) + point.y;
      let p1 = new THREE.Vector3(x1, y1, 0);
      let x2 = radius * Math.cos(angle * (i + 1)) + point.x;
      let y2 = radius * Math.sin(angle * (i + 1)) + point.y;
      let p2 = new THREE.Vector3(x2, y2, 0);
      positions.push(p1, p2, point);
    }
    geo.setFromPoints(positions);
    let material = new THREE.MeshBasicMaterial({ color: this.m_color });
    let tempObject = new THREE.Mesh(geo, material);
    this.m_tempObjectGroup.add(tempObject);
  }
  private clear() {
    this.m_context.ToolsManager.GroupClear(this.m_tempObjectGroup);
    this.m_context.ToolsManager.ObjectsClear([this.m_locationObject]);
  }

  private endHandle() {
    let key = this.m_layer.Id + "_" + this.m_color;
    let meshs: DbMesh[] = this.m_context.ModelManager.Meshs[key]?.meshs ?? [];
    this.m_tempObjectGroup.children.forEach((item, i) => {
      if (item instanceof THREE.Mesh) {
        let position = Array.from(
          item.geometry.attributes.position.array
        ) as number[];
        let index = item.geometry.index as number[];
        let dbMesh = new DbMesh({
          id: generateUUID(),
          layerId: this.m_layer.Id,
          blockId: "",
          vertexs: new Float32Array(position),
          indexes: new Int32Array(index),
          color: this.m_color,
          extend_id: generateUUID(),
        });
        dbMesh.getBoundingBox();
        dbMesh.getBoundingSphere();
        meshs.push(dbMesh);
        this.m_returnData.push({
          object: dbMesh,
          point: this.m_points[i],
        });
      }
    });

    if (this.m_context.ModelManager.Meshs[key]) {
      this.m_context.ModelManager.Meshs[key].meshs = meshs;
      this.m_context.ToolsManager.MeshToModel(
        meshs,
        this.m_context.ModelManager.Meshs[key].model
      );
    } else {
      this.m_context.ModelManager.Meshs[key] = {
        meshs: meshs,
        canEdit: true,
        model: this.m_context.ModelManager.addMeshToModel(
          this.m_layer.Id,
          this.m_color,
          meshs
        ),
      };
    }
  }

  private get CurDir() {
    return this.m_curPoint.clone().sub(this.m_points[0]);
  }
  private get Scale() {
    return this.m_context.ViewManager.ScreenModelScale;
  }
}

export { InsertConnectPointModify };
